import { apiTeams } from "@/api/team.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { ApiErrorException, ApiPassException } from "@/types/api.types.ts";
import {
  ApiTeam,
  ApiTeamFilter,
  ApiTeamSavePayload,
  TeamFilterDefault,
} from "@/types/team.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import {
  useInfiniteQuery,
  useMutation,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Control, FieldValues } from "react-hook-form";

const teamsKeys = createQueryKeys("teams", {
  detail: (id: number | undefined) => ({
    queryKey: [id],
    queryFn: async () => {
      if (id == undefined) throw new ApiPassException("Team id is required");
      const result = await apiTeams.getById(id);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
  searchInfinite: (filter: ApiTeamFilter) => ({ queryKey: [filter] }),
  search: (filter: ApiTeamFilter) => ({ queryKey: [filter] }),
});

export const useTeamSearch = (filter: ApiTeamFilter, enabled: boolean = true) => {
  return useQuery({
    ...teamsKeys.search(filter),
    enabled: enabled,
    queryFn: async ({ signal }) => {
      const result = await apiTeams.search({ ...filter }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
  });
};

export const useTeamSearchInline = (enabled = true) => {
  const [filter, setFilter] = useState<ApiTeamFilter>(TeamFilterDefault);
  const { data, isLoading } = useTeamSearch(filter, enabled);
  const options = useMemo(() => {
    return data?.items || ([] as ApiTeam[]);
  }, [data]);

  function search(text: string | undefined) {
    setFilter({ ...filter, text: text });
  }

  return {
    data,
    isLoading,
    options,
    search,
  };
};

export const useTeamSearchInfinite = (filter: ApiTeamFilter, enabled: boolean = true) => {
  return useInfiniteQuery({
    ...teamsKeys.searchInfinite(filter),
    queryFn: async ({ pageParam = 1, signal }) => {
      const result = await apiTeams.search({ ...filter, page_number: pageParam }, signal);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    enabled,
    initialPageParam: 1,
    getNextPageParam: (lastPageParam) =>
      lastPageParam && lastPageParam.metadata.page_number < lastPageParam.metadata.total_pages
        ? lastPageParam.metadata.page_number + 1
        : undefined,
    placeholderData: (prevData) => prevData,
  });
};

export const useTeamDetails = (id: number | undefined, enabled = true) => {
  return useQuery({
    ...teamsKeys.detail(id),
    enabled: !!id && enabled,
  });
};

export const useTeamsByIds = (ids: number[], enabled = true) => {
  const queries = useQueries({
    queries: ids.map((id) => ({
      ...teamsKeys.detail(id),
      enabled,
    })),
  });

  return {
    data: queries.flatMap((query) => (query.data ? [query.data] : [])),
    isLoading: queries.some((query) => query.isLoading),
  };
};

export interface SaveParams {
  id: number | undefined;
  payload: ApiTeamSavePayload;
}

export const useTeamSave = <
  TFieldValues extends FieldValues = FieldValues,
  TContext = unknown,
  TTransformedValues = TFieldValues,
>(
  control: Control<TFieldValues, TContext, TTransformedValues> | undefined = undefined
) => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, payload }: SaveParams) => {
      const result = id ? await apiTeams.update(id, payload) : await apiTeams.create(payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: (data: ApiTeam) => {
      queryClient.invalidateQueries({ queryKey: teamsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: teamsKeys.searchInfinite._def });
      queryClient.setQueryData(teamsKeys.detail(data.id).queryKey, data);
    },
    onError: (error: Error) => {
      onError(error, control);
    },
  });
};

export const useTeamDelete = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (teamId: number) => {
      const { success, error } = await apiTeams.delete(teamId);

      if (error || !success) throw new ApiErrorException(error);

      return success;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: teamsKeys.search._def });
      queryClient.invalidateQueries({ queryKey: teamsKeys.searchInfinite._def });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export interface TeamSetBackgroundParams {
  fileUUID: string;
  id: number;
}

export const useTeamSetBackground = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async ({ id, fileUUID }: TeamSetBackgroundParams) => {
      const result = await apiTeams.setBackground(id, fileUUID);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: (result, variables) => {
      queryClient.setQueryData<ApiTeam>(teamsKeys.detail(variables.id).queryKey, (oldData) => {
        if (oldData) return { ...oldData, background_url: result.url };
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useTeamDeleteBackground = () => {
  const queryClient = useQueryClient();
  const { onError } = useErrorHandle();

  return useMutation({
    mutationFn: async (teamId: number) => {
      const result = await apiTeams.deleteBackground(teamId);

      if (result.error || !result.data) throw new ApiErrorException(result.error);

      return result.data;
    },
    onSuccess: (_, teamId) => {
      queryClient.setQueryData<ApiTeam>(teamsKeys.detail(teamId).queryKey, (oldData) => {
        if (oldData) return { ...oldData, background_url: null };
      });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
